import { useActiveRightStackJid } from '@/app/store';
import { updateMediaStatus } from '@/features/chats/model/conversation/reducer';
import { handleMediaAction } from '@/features/chats/services/messageQueueManager';
import { DOWNLOAD_STATUS, mediaStatusConstants, UPLOAD_STATUS } from '@/shared/constants';
import { blockOfflineAction, getUserIdFromJid, logger } from '@/shared/utils';
import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

const UPLOADING_STATUSES = new Set([UPLOAD_STATUS.PENDING, UPLOAD_STATUS.IN_PROGRESS, UPLOAD_STATUS.QUEUED]);

const determineMediaStatus = (uploadStatus, downloadStatus, blobUrl) => {
   if (downloadStatus === DOWNLOAD_STATUS.IN_PROGRESS) return mediaStatusConstants.DOWNLOADING;
   if (UPLOADING_STATUSES.has(uploadStatus)) return mediaStatusConstants.UPLOADING;
   if (uploadStatus === UPLOAD_STATUS.FAILED) return mediaStatusConstants.NOT_UPLOADED;

   const uploadDone = uploadStatus === UPLOAD_STATUS.COMPLETED;
   if (uploadDone && downloadStatus === DOWNLOAD_STATUS.COMPLETED) return mediaStatusConstants.LOADED;
   if (uploadDone && !blobUrl) return mediaStatusConstants.NOT_DOWNLOADED;

   return mediaStatusConstants.UNKNOWN;
};

/**
 * Custom hook for managing media group progress and aggregate status.
 *
 * Progress is calculated as a simple count-based average of each file's
 * individual 0-100 progress value. This matches how the upload/download
 * listeners work (each emits { msgId, progress: 0-100 }) and avoids
 * dependence on file sizes for the progress bar.
 *
 * Example: 3 files at 50%, 0%, 100% → group progress = (50 + 0 + 100) / 3 = 50%
 *
 * @param {Array} messages - Array of media messages in the group.
 */
export const useMediaGroupProgress = (messages = []) => {
   const dispatch = useDispatch();
   const chatId = getUserIdFromJid(useActiveRightStackJid());

   // Subscribe only to progress entries that belong to this group
   const progressData = useSelector(state => {
      const all = state.mediaProgressData;
      return messages.reduce((acc, msg) => {
         const entry = all[msg.msgId];
         if (entry !== undefined) acc[msg.msgId] = entry;
         return acc;
      }, {});
   }, shallowEqual);

   const { progress, totalFileSize } = useMemo(() => {
      let totalLoadedBytes = 0;
      let totalSize = 0;

      messages.forEach(msg => {
         const { file_size: fileSize = 0, is_uploading: up, is_downloaded: dl, blobUrl } = msg.msgBody?.media ?? {};

         const status = determineMediaStatus(up, dl, blobUrl);

         totalSize += fileSize;

         if (status === mediaStatusConstants.LOADED) {
            // Unconditionally consider 100% loaded if LOADED status
            totalLoadedBytes += fileSize;
         } else if (status === mediaStatusConstants.DOWNLOADING || status === mediaStatusConstants.UPLOADING) {
            const entry = progressData[msg.msgId];
            if (entry) {
               if (status === mediaStatusConstants.DOWNLOADING && entry?.downloadedBytes != null) {
                  // True byte progression from SDK
                  // Capped to fileSize in case SDK exceeds the originally reported file size
                  totalLoadedBytes += Math.min(entry.downloadedBytes, fileSize);
               } else if (status === mediaStatusConstants.UPLOADING && entry?.uploadedBytes != null) {
                  totalLoadedBytes += Math.min(entry.uploadedBytes, fileSize);
               }
            }
         }
         // NOT_DOWNLOADED / NOT_UPLOADED / UNKNOWN → 0 loaded bytes
      });

      return {
         // Consolidated true progress based on bytes
         progress: totalSize > 0 ? Math.round((totalLoadedBytes / totalSize) * 100) : 0,
         totalFileSize: totalSize,
      };
   }, [messages, progressData]);

   const mediaStatus = useMemo(() => {
      let hasDownloading = false;
      let hasUploading = false;
      let hasFailedUpload = false;
      let allLoaded = true;
      let hasDownloadable = false;

      messages.forEach(msg => {
         const { is_uploading: up, is_downloaded: dl, blobUrl } = msg.msgBody?.media ?? {};
         const status = determineMediaStatus(up, dl, blobUrl);

         if (status === mediaStatusConstants.DOWNLOADING) hasDownloading = true;
         if (status === mediaStatusConstants.UPLOADING) hasUploading = true;
         if (status === mediaStatusConstants.NOT_UPLOADED) hasFailedUpload = true;
         if (status !== mediaStatusConstants.LOADED) allLoaded = false;
         if (status === mediaStatusConstants.NOT_DOWNLOADED) hasDownloadable = true;
      });

      if (hasDownloading) return mediaStatusConstants.DOWNLOADING;
      if (hasUploading) return mediaStatusConstants.UPLOADING;
      if (hasFailedUpload) return mediaStatusConstants.NOT_UPLOADED;
      if (allLoaded) return mediaStatusConstants.LOADED;
      if (hasDownloadable) return mediaStatusConstants.NOT_DOWNLOADED;

      return mediaStatusConstants.UNKNOWN;
   }, [messages]);

   const isProgressing =
      mediaStatus === mediaStatusConstants.UPLOADING || mediaStatus === mediaStatusConstants.DOWNLOADING;
   const isRetryable = mediaStatus === mediaStatusConstants.NOT_UPLOADED;
   const isDownloadable = mediaStatus === mediaStatusConstants.NOT_DOWNLOADED && !isProgressing;

   const batchDownload = useCallback(async () => {
      if (blockOfflineAction()) return;

      const setDownloadStatus = (msgId, status, extra = {}) =>
         dispatch(
            updateMediaStatus({
               chatId,
               msgId,
               is_uploading: UPLOAD_STATUS.COMPLETED,
               is_downloaded: status,
               ...extra,
            }),
         );

      const downloadTasks = messages
         .filter(msg => {
            const { is_uploading: up, is_downloaded: dl, blobUrl } = msg.msgBody?.media ?? {};
            return determineMediaStatus(up, dl, blobUrl) === mediaStatusConstants.NOT_DOWNLOADED;
         })
         .map(async msg => {
            setDownloadStatus(msg.msgId, DOWNLOAD_STATUS.IN_PROGRESS);
            try {
               const result = await handleMediaAction(msg, 'download', false);
               const succeeded = result?.statusCode === 200;
               setDownloadStatus(
                  msg.msgId,
                  succeeded ? DOWNLOAD_STATUS.COMPLETED : DOWNLOAD_STATUS.FAILED,
                  succeeded && result?.blobUrl ? { blobUrl: result.blobUrl } : {},
               );
            } catch (err) {
               setDownloadStatus(msg.msgId, DOWNLOAD_STATUS.FAILED);
               logger.error('[useMediaGroupProgress] Batch download error:', err);
            }
         });

      await Promise.all(downloadTasks);
   }, [messages, dispatch, chatId]);

   const batchCancel = useCallback(async () => {
      // Only cancel messages that are actively in-progress within this group.
      const cancelTasks = messages.map(async msg => {
         const { is_uploading: up, is_downloaded: dl } = msg.msgBody?.media ?? {};
         try {
            if (UPLOADING_STATUSES.has(up)) {
               await handleMediaAction(msg, 'cancelUpload');
            }
            if (dl === DOWNLOAD_STATUS.IN_PROGRESS) {
               await handleMediaAction(msg, 'cancelDownload');
            }
         } catch (error) {
            logger.error(`[useMediaGroupProgress] error canceling msgId: ${msg.msgId}`, error);
         }
      });

      await Promise.all(cancelTasks);
   }, [messages]);

   const batchRetry = useCallback(() => {
      if (blockOfflineAction()) {
         return;
      }

      messages.forEach(msg => {
         const { is_uploading: up, is_downloaded: dl, blobUrl } = msg.msgBody?.media ?? {};
         const status = determineMediaStatus(up, dl, blobUrl);
         if (status === mediaStatusConstants.NOT_UPLOADED) {
            try {
               handleMediaAction(msg, 'resumeUpload');
            } catch (error) {
               logger.error(`[useMediaGroupProgress] error resuming upload for msgId: ${msg.msgId}`, error);
            }
         }
      });

      batchDownload();
   }, [messages, batchDownload]);

   return {
      mediaStatus,
      progress,
      totalFileSize,
      isProgressing,
      isRetryable,
      isDownloadable,
      batchDownload,
      batchCancel,
      batchRetry,
   };
};

export default useMediaGroupProgress;
